import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { useDispatch, useSelector } from "react-redux";
import {
  handleRemoveTemplate,
  handleSaveScheduleValuesTemplate,
  fetchSovTemplates,
  setScheduleOfValues,
  setUsingTemplates,
  updateScheduleValuesSignedUrl,
  updateJobCost,
} from "../../../store/actions/uiActions/myPermitsActions";

const ZEROS_LIST = ["", ".", "0", ".0", ".00", "0.", "0.0", "0.00"];
const ROUNDING_EPSILON = 0.000001;

const clampNearZero = (value) =>
  Math.abs(value) < ROUNDING_EPSILON ? 0 : value;

export const formatMoneyValue = (value) => {
  if (value === "") {
    return "";
  }
  // Remove any non-numeric characters except the decimal point
  value = value.replace(/[^\d.]/g, "");

  // Split the input into integer and decimal parts
  let [integer, decimal] = value.split(".");

  // Limit the integer part to 10 digits
  integer = integer ? integer.slice(0, 10) : "";

  // Format the integer part with commas
  let formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle cases for decimal parts
  if (decimal === undefined) {
    // If there's no decimal part, ensure it ends with ".00"
    formattedInteger += ".00";
  } else if (decimal.length === 0) {
    // If the decimal part is empty, assume ".00"
    formattedInteger += ".00";
  } else if (decimal.length === 1) {
    // If there's only one decimal digit, add a trailing "0"
    formattedInteger += `.${decimal}0`;
  } else {
    // Otherwise, limit the decimal part to two digits
    formattedInteger += `.${decimal.slice(0, 2)}`;
  }

  // Add the dollar sign
  return `${formattedInteger}`;
};

const SortableRow = ({
  id,
  index,
  row,
  handleInputChange,
  handleDeleteSingle,
  currentRow,
  setCurrentRow,
  titleInputRef,
  formatMoneyValue,
  editableRows,
  setEditableRows,
  handleAddRow,
  addRowMax,
  totalCost,
  savingTemplate,
}) => {
  const { rows } = useSelector(
    (state) => state.myPermitsComponent.scheduleOfValues,
  );

  const { setNodeRef, transform, transition, listeners, attributes } =
    useSortable({ id });

  const dispatch = useDispatch();

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        borderTop: index === 0 ? undefined : "2px solid #d3d3d3",
        backgroundColor:
          currentRow.includes(index) && !savingTemplate ? "#d1f9ec" : undefined,
        transition,
        "&:hover": {
          backgroundColor:
            currentRow.includes(index) || savingTemplate
              ? undefined
              : "#f1fffa", // 💡 your hover color
        },
      }}
      onClick={(e) => {
        if (e.ctrlKey) {
          if (!currentRow.includes(index)) {
            setCurrentRow((prev) => [...prev, index]);
          }
        } else {
          if (
            currentRow.length !== 1 ||
            (currentRow.length === 1 && index !== currentRow[0])
          ) {
            setCurrentRow([index]);
          }
        }
      }}
    >
      <TableCell sx={{ p: 0, pt: 0.6, width: "20px" }}>
        <MoreVertIcon
          disabled={savingTemplate}
          sx={{
            cursor: "grab",
            outline: "none",
            "&:focus": {
              outline: "none",
            },
            "&:focus-visible": {
              outline: "none",
            },
          }}
          {...attributes}
          {...listeners}
        />
      </TableCell>
      {/* TITLE INPUT */}
      <TableCell sx={{ p: 0 }}>
        <FormControl
          disabled={savingTemplate}
          variant="outlined"
          sx={{ width: "100%" }}
          onDoubleClick={(e) => {
            const input = e.currentTarget.querySelector("input");
            input?.focus();
            input?.select();

            setEditableRows((prev) => ({
              ...prev,
              title: new Set(prev.title).add(row.id),
            }));

            if (
              currentRow.length !== 1 ||
              (currentRow.length === 1 && index !== currentRow[0])
            ) {
              setCurrentRow([index]);
            }
          }}
        >
          <OutlinedInput
            inputRef={(el) => {
              if (!titleInputRef.current[row.id])
                titleInputRef.current[row.id] = {};
              titleInputRef.current[row.id].title = el;
            }}
            id={`row-title-input-${row.id}`}
            size="small"
            value={row.title || ""}
            onChange={(e) => handleInputChange(index, "title", e.target.value)}
            onFocus={() => {
              if (!editableRows.title.has(row.id)) {
                setEditableRows((prev) => ({
                  ...prev,
                  title: new Set(prev.title).add(row.id),
                }));
              }
            }}
            onBlur={(e) => {
              if (e.target.value === "") {
                const newRows = rows.map((row, i) =>
                  i === index ? { ...row, title: "Enter new title" } : row,
                );

                dispatch(setScheduleOfValues({ rows: newRows }));
              }
              setEditableRows((prev) => {
                const updated = new Set(prev.title);
                updated.delete(row.id);
                return {
                  ...prev,
                  title: updated,
                };
              });
            }}
            onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     e.preventDefault();
              //     e.currentTarget.blur();
              //   }else
              if (e.key === "+") {
                e.preventDefault();
                if (rows?.length === 23 || totalCost === 0 || addRowMax) {
                  return;
                } else {
                  handleAddRow();
                }
              } else {
                if (e.key === "Tab" && !e.shiftKey) {
                  e.preventDefault();
                  titleInputRef.current[row.id]?.percentage?.focus();
                  titleInputRef.current[row.id]?.percentage?.select?.();
                }

                if (e.shiftKey && e.key === "Tab") {
                  e.preventDefault(); // stop it from escaping the title
                }

                if (e.ctrlKey && !e.shiftKey && e.key === "Control") {
                  e.preventDefault(); // don't go back from title
                }
              }
            }}
            sx={{
              "& input": {
                textAlign: "center",
                fontSize: "1rem",
                cursor: editableRows.title.has(row.id) ? "text" : "default",
              },
              "& .MuiInputAdornment-root": {
                pointerEvents: "auto",
                cursor: "default",
                "& .MuiTypography-root": {
                  color: "#000",
                  fontSize: "1rem",
                },
              },
              "& .MuiOutlinedInput-input": {
                zIndex: 1,
                cursor: "default",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: 0,
                borderColor: "transparent", // default no border
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent", // no border on hover
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0db87a", // 💥 your desired color here (e.g. blue)
                borderWidth: "1px", // optional: define thickness
              },
              "&.Mui-focused input": {
                cursor: "text", // change to text cursor when focused
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-focused": {
                backgroundColor: "transparent",
              },
              backgroundColor: "transparent",
              cursor: "default",
              "&.MuiInputBase-root": {
                pointerEvents: editableRows.title.has(row.id) ? "auto" : "none",
              },
            }}
          />
        </FormControl>
      </TableCell>
      {/* PERCENTAGE INPUT */}
      <TableCell sx={{ p: 0, width: "110px" }}>
        <FormControl
          disabled={savingTemplate}
          variant="outlined"
          onDoubleClick={(e) => {
            const input = e.currentTarget.querySelector("input");
            input?.focus();
            input?.select();

            setEditableRows((prev) => ({
              ...prev,
              percentage: new Set(prev.percentage).add(row.id),
            }));

            if (
              currentRow.length !== 1 ||
              (currentRow.length === 1 && index !== currentRow[0])
            ) {
              setCurrentRow([index]);
            }
          }}
        >
          <OutlinedInput
            inputRef={(el) => {
              if (!titleInputRef.current[row.id])
                titleInputRef.current[row.id] = {};
              titleInputRef.current[row.id].percentage = el;
            }}
            id={`row-percentage-input-${row.id}`}
            size="small"
            value={row.displayPercentage ?? ""}
            onChange={(e) =>
              handleInputChange(index, "percentage", e.target.value)
            }
            endAdornment={<InputAdornment position="end">%</InputAdornment>}
            onFocus={() => {
              if (!editableRows.percentage.has(row.id)) {
                setEditableRows((prev) => ({
                  ...prev,
                  percentage: new Set(prev.percentage).add(row.id),
                }));
              }
            }}
            onBlur={(e) => {
              const newRows = rows.map((r) => ({ ...r }));
              const newVal = e.target.value === "" ? 0 : e.target.value;

              newRows[index]["displayPercentage"] = String(
                parseFloat(newVal).toFixed(2),
              );
              dispatch(setScheduleOfValues({ rows: newRows }));

              setEditableRows((prev) => {
                const updated = new Set(prev.percentage);
                updated.delete(row.id);
                return {
                  ...prev,
                  percentage: updated,
                };
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "+") {
                e.preventDefault();
                if (rows?.length === 23 || totalCost === 0 || addRowMax) {
                  return;
                } else {
                  handleAddRow();
                }
              } else {
                if (e.key === "Tab" && !e.shiftKey) {
                  e.preventDefault();
                  titleInputRef.current[row.id]?.amount?.focus();
                  titleInputRef.current[row.id]?.amount?.select?.();
                }

                if (e.ctrlKey && !e.shiftKey && e.key === "Control") {
                  e.preventDefault();
                  titleInputRef.current[row.id]?.title?.focus();
                  titleInputRef.current[row.id]?.title?.select?.();
                }
              }
            }}
            sx={{
              "& input": {
                textAlign: "center",
                fontSize: "1rem",
                cursor: editableRows.percentage.has(row.id)
                  ? "text"
                  : "default",
              },
              "& .MuiInputAdornment-root": {
                pointerEvents: "auto",
                cursor: "default",
                "& .MuiTypography-root": {
                  color: "#000",
                  fontSize: "1rem",
                },
              },
              "& .MuiOutlinedInput-input": {
                zIndex: 1,
                cursor: "default",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: 0,
                borderColor: "transparent",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0db87a",
                borderWidth: "1px",
              },
              "&.Mui-focused input": {
                cursor: "text",
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-focused": {
                backgroundColor: "transparent",
              },
              backgroundColor: "transparent",
              cursor: "default",
              "&.MuiInputBase-root": {
                pointerEvents: editableRows.percentage.has(row.id)
                  ? "auto"
                  : "none",
              },
            }}
          />
        </FormControl>
      </TableCell>
      {/* AMOUNT INPUT */}
      <TableCell sx={{ p: 0, width: "150px" }}>
        <FormControl
          disabled={savingTemplate}
          variant="outlined"
          onDoubleClick={(e) => {
            const input = e.currentTarget.querySelector("input");
            input?.focus();
            input?.select();

            setEditableRows((prev) => ({
              ...prev,
              amount: new Set(prev.amount).add(row.id),
            }));

            if (
              currentRow.length !== 1 ||
              (currentRow.length === 1 && index !== currentRow[0])
            ) {
              setCurrentRow([index]);
            }
          }}
        >
          <OutlinedInput
            inputRef={(el) => {
              if (!titleInputRef.current[row.id])
                titleInputRef.current[row.id] = {};
              titleInputRef.current[row.id].amount = el;
            }}
            id={`row-amount-input-${row.id}`}
            size="small"
            value={row.displayAmount ?? ""}
            onChange={(e) => handleInputChange(index, "amount", e.target.value)}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            onFocus={() => {
              if (!editableRows.amount.has(row.id)) {
                setEditableRows((prev) => ({
                  ...prev,
                  amount: new Set(prev.amount).add(row.id),
                }));
              }
            }}
            onBlur={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              const parsed = parseFloat(raw);
              const formatted = isNaN(parsed)
                ? ""
                : formatMoneyValue(String(parsed));

              const newRows = rows.map((r) => ({ ...r }));
              newRows[index]["displayAmount"] = formatted;

              dispatch(setScheduleOfValues({ rows: newRows }));

              setEditableRows((prev) => {
                const updated = new Set(prev.amount);
                updated.delete(row.id);
                return {
                  ...prev,
                  amount: updated,
                };
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "+") {
                e.preventDefault();
                if (rows?.length === 23 || totalCost === 0 || addRowMax) {
                  return;
                } else {
                  handleAddRow();
                }
              } else {
                if (e.key === "Tab" && !e.shiftKey) {
                  e.preventDefault(); // don’t allow tab to exit
                }

                if (e.ctrlKey && !e.shiftKey && e.key === "Control") {
                  e.preventDefault();
                  titleInputRef.current[row.id]?.percentage?.focus();
                  titleInputRef.current[row.id]?.percentage?.select?.();
                }
              }
            }}
            sx={{
              "& input": {
                textAlign: "center",
                fontSize: "1rem",
                cursor: editableRows.amount.has(row.id) ? "text" : "default",
              },
              "& .MuiInputAdornment-root": {
                pointerEvents: "auto",
                cursor: "default",
                "& .MuiTypography-root": {
                  color: "#000",
                  fontSize: "1rem",
                },
              },
              "& .MuiOutlinedInput-input": {
                zIndex: 1, // keeps input above base
                cursor: "default",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: 0,
                borderColor: "transparent",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0db87a",
                borderWidth: "1px",
              },
              "&.Mui-focused input": {
                cursor: "text", // override for actual focus
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-focused": {
                backgroundColor: "transparent",
              },
              backgroundColor: "transparent",
              cursor: "default",
              "&.MuiInputBase-root": {
                pointerEvents: editableRows.amount.has(row.id)
                  ? "auto"
                  : "none",
              },
            }}
          />
        </FormControl>
      </TableCell>
      <TableCell sx={{ p: 0, width: "20px" }}>
        <IconButton
          disabled={savingTemplate}
          onClick={() => handleDeleteSingle(index)}
        >
          <DeleteIcon color={savingTemplate ? "grey" : "error"} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const CreatingScheduleOfValues = ({
  addRowMax,
  setAddRowMax,
  setFile,
  savingTemplate,
}) => {
  const { selectedRow, sovTemplates } = useSelector(
    (state) => state.myPermitsComponent,
  );
  const { isUsingTemplate, templateName, removedTemplate } = useSelector(
    (state) => state.myPermitsComponent.usingTemplate,
  );

  const {
    rows,
    scheduleCreated,
    remainingAmount,
    remainingPercentage,
    totalCost,
    totalCostDisplay,
    loadedLogo,
    savingTemplateLoading,
  } = useSelector((state) => state.myPermitsComponent.scheduleOfValues);

  const [currentRow, setCurrentRow] = useState([]);
  const [invalidJobCostText, setInvalidJobCostText] = useState("");
  const [logoUploaded, setLogoUploaded] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [tempTemplateName, setTempTemplateName] = useState("");

  const dispatch = useDispatch();

  const [editableRows, setEditableRows] = useState({
    title: new Set(),
    percentage: new Set(),
    amount: new Set(),
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const scrollRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const saveTemplateInputRef = React.useRef(null);
  const hasLogo = (logoUploaded && logoPreview) || Boolean(loadedLogo);

  const titleInputRef = React.useRef({});

  const sensors = useSensors(useSensor(PointerSensor));

  const isSignedUrlExpired = (url) => {
    const match = url.match(/Expires=(\d+)/);
    if (!match) return true;

    const expiresAt = parseInt(match[1], 10);
    const now = Math.floor(Date.now() / 1000);

    return now >= expiresAt;
  };

  // Reset locally managed logo preview when values are cleared from Redux
  useEffect(() => {
    if (rows?.length === 0 && !scheduleCreated && !Boolean(loadedLogo)) {
      if (logoUploaded || logoPreview) {
        setLogoUploaded(false);
        setLogoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    }
  }, [rows?.length, scheduleCreated, loadedLogo, logoUploaded, logoPreview]);

  // Cache-busted SOV preview URL and simple retry on error
  const sovDocument = selectedRow?.documents?.sov || {};
  useEffect(() => {
    const url = sovDocument?.signed_url || "";
    if (url) {
      const sep = url.includes("?") ? "&" : "?";
      void `${url}${sep}cb=${Date.now()}`;
    }
  }, [sovDocument?.signed_url]);

  const handleDragStart = useCallback(
    (event) => {
      const activeId = event.active.id;
      const draggingIndex = rows?.findIndex((r) => r.id === activeId);

      if (!currentRow.includes(draggingIndex)) {
        setCurrentRow([draggingIndex]);
      }
    },
    [currentRow, rows],
  );

  useEffect(() => {
    if (sovDocument?.signed_url) {
      const urlExpired = isSignedUrlExpired(sovDocument?.signed_url);

      if (urlExpired) {
        dispatch(updateScheduleValuesSignedUrl());
      }

      if (rows?.length === 0) {
        const rawRows = sovDocument?.rows;
        const isValidArray = Array.isArray(rawRows);

        const newRows = isValidArray
          ? rawRows.filter((r) => r && typeof r === "object" && "id" in r)
          : [];

        const total = newRows?.reduce(
          (sum, row) => sum + (parseFloat(row.amount) || 0),
          0,
        );

        const logoUrl = sovDocument?.logo_signed_url;

        // if (logoUrl) {
        //   setLogoUploaded(true);
        // }

        const formattedTotal = formatMoneyValue(String(total));
        dispatch(
          setScheduleOfValues({
            scheduleCreated: true,
            rows: newRows,
            totalCost: total,
            totalCostDisplay: formattedTotal,
            remainingAmount: 0.0,
            remainingPercentage: 0.0,
            loadedLogo: logoUrl,
          }),
        );

        return;
      }

      if (scheduleCreated) {
        dispatch(setScheduleOfValues({ scheduleCreated: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    rows?.length,
    scheduleCreated,
    sovDocument?.signed_url,
    sovDocument?.rows,
    sovDocument?.logo_signed_url,
  ]);

  // Sync total cost to job_cost field
  useEffect(() => {
    if (totalCost > 0) {
      dispatch(updateJobCost(totalCost));
    }
  }, [totalCost, dispatch]);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over) return;

      const fromIndex = rows?.findIndex((r) => r.id === active.id);
      const toIndex = rows?.findIndex((r) => r.id === over.id);

      if (fromIndex === -1 || toIndex === -1) return;

      // Sort selected indices to move them in order
      const sortedIndices = [...currentRow].sort((a, b) => a - b);

      // Grab selected rows
      const selectedRows = sortedIndices.map((i) => ({ ...rows[i] }));

      // Remove selected rows
      let tempRows = rows?.filter((_, i) => !sortedIndices.includes(i));

      // Insert at new index
      let insertAt = toIndex;
      if (toIndex > sortedIndices[0]) {
        insertAt = toIndex - sortedIndices.length + 1;
      }

      tempRows.splice(insertAt, 0, ...selectedRows);
      dispatch(setScheduleOfValues({ rows: tempRows }));

      // Update selection
      setCurrentRow(sortedIndices.map((_, i) => insertAt + i));
    },
    [rows, currentRow, dispatch],
  );

  const isValidNumber = (val) => {
    const trimmed = val.toString().trim();
    return /^-?\d*(\.?\d{0,2})?$/.test(trimmed);
  };

  const calculateRemainings = useCallback(
    ({ newTotalCost = totalCost, newRows = rows } = {}) => {
      const usedTotalCost = newRows?.reduce(
        (acc, r) => acc + (parseFloat(r.amount) || 0),
        0,
      );

      //? NEW REMAINING AMOUNTS ///////////// <-- remaining amounts to be full total
      let newRemainingAmount = clampNearZero(newTotalCost - usedTotalCost);
      let newRemainingPercent =
        newTotalCost > 0
          ? clampNearZero((newRemainingAmount / newTotalCost) * 100)
          : 0;

      newRemainingPercent = Math.max(0, Math.min(100, newRemainingPercent));
      if (newRemainingPercent === 0) {
        newRemainingAmount = 0;
      }
      //? /////////////////////////////////////////////////////

      //? NEW ROW AMOUNTS ///////////////////////////////////
      const newRowAmount = newTotalCost - newRemainingAmount;
      const newRowPercent = 100 - newRemainingPercent;
      //? /////////////////////////////////////////////////////

      const displayPercentage = String(newRemainingPercent);
      const displayAmount = formatMoneyValue(String(newRemainingAmount));

      dispatch(
        setScheduleOfValues({
          remainingAmount: newRemainingAmount,
          remainingPercentage: newRemainingPercent,
        }),
      );

      return {
        displayPercentage,
        displayAmount,
        newRowPercent,
        newRowAmount,
      };
    },
    [rows, totalCost, dispatch],
  );

  const handleAddRow = useCallback(() => {
    const newRowId = crypto.randomUUID();

    const newRows = (Array.isArray(rows) ? rows : []).map((r) => ({ ...r }));

    const usedTotalCost = newRows?.reduce(
      (acc, r) => acc + (parseFloat(r.amount) || 0),
      0,
    );

    let newRow = {
      id: newRowId,
      title: "Enter new title",
      percentage: 100.0,
      amount: totalCost,
      displayPercentage: "100.00",
      displayAmount: formatMoneyValue(String(totalCost)),
    };

    if (usedTotalCost >= totalCost) {
      return;
    }

    if (newRows.length > 0) {
      const newAmount = totalCost - usedTotalCost;
      const newAmountPercent = (newAmount / totalCost) * 100;

      newRow.percentage = newAmountPercent;
      newRow.amount = newAmount;
      newRow.displayPercentage = String(newAmountPercent.toFixed(2));
      newRow.displayAmount = formatMoneyValue(String(newAmount));
    }

    newRows.push(newRow);

    calculateRemainings({ newRows });

    setCurrentRow([newRows.length - 1]);
    dispatch(setScheduleOfValues({ rows: newRows }));

    // Scroll happens on next frame (after DOM update)
    setTimeout(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });

      const input = titleInputRef.current[newRowId]?.title;
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  }, [calculateRemainings, rows, dispatch, totalCost]);

  const handleDeleteSingle = (index) => {
    const newRows = rows.map((r) => ({ ...r }));
    const newCurrentRow = index === rows?.length - 1 ? index - 1 : index;

    newRows.splice(index, 1);

    calculateRemainings({ newRows });
    dispatch(setScheduleOfValues({ rows: newRows }));
    setCurrentRow([newCurrentRow]);
  };

  const isOnlyZeros = (str) => {
    const cleaned = str.replace(/[^0-9]/g, ""); // remove commas, dots etc.
    return cleaned.length > 0 && /^[0]+$/.test(cleaned);
  };

  const cleanLeadingZeros = (str) => {
    const original = str;
    const cleaned = str.replace(/[^0-9.]/g, ""); // remove commas etc.

    if (!cleaned.includes(".")) {
      const fixed = String(parseInt(cleaned || "0", 10));
      return {
        cleanedValue: fixed,
        cleanedSomething: fixed !== original,
      };
    }

    const endsWithDot = cleaned.endsWith(".");
    const [intPart, decimalPart = ""] = cleaned.split(".");
    const fixedInt = String(parseInt(intPart || "0", 10));

    let fixed;
    if (endsWithDot) {
      fixed = `${fixedInt}.`;
    } else {
      fixed = decimalPart ? `${fixedInt}.${decimalPart}` : fixedInt;
    }

    return {
      cleanedValue: fixed,
      cleanedSomething: fixed !== original,
    };
  };

  const handleBlur = (value) => {
    if (invalidJobCostText !== "") {
      setInvalidJobCostText("");
    }
    const zerosOnly = isOnlyZeros(value);

    if (ZEROS_LIST.includes(value) || zerosOnly) {
      if (rows?.length === 0) {
        dispatch(
          setScheduleOfValues({
            totalCostDisplay: "",
          }),
        );
        return;
      }
    }

    let newValue = value;

    if (newValue.startsWith(".")) {
      newValue = "0" + newValue;
    }
    if (rows?.length > 0) {
      if (ZEROS_LIST.includes(value) || zerosOnly) {
        newValue = totalCost;
      }
    }

    const formattedValue = formatMoneyValue(String(newValue));
    dispatch(
      setScheduleOfValues({
        totalCostDisplay: formattedValue,
      }),
    );
  };

  const handleChangeAmount = (value) => {
    const input = document.getElementById("outlined-adornment-amount");
    const selectionStart = input?.selectionStart;
    const originalLength = value.length;
    const originalCursor = selectionStart;
    const offsetFromEnd = originalLength - originalCursor;

    const rowsSize = rows?.length;

    let newStringValue = "";

    if (value === "") {
      newStringValue = "0";
    } else {
      newStringValue = value.replace(/\s+/g, "");
    }

    if (
      newStringValue.length > 1 &&
      newStringValue.charAt(0) === "0" &&
      newStringValue.charAt(1) !== "."
    ) {
      newStringValue = newStringValue.slice(1);
    }

    const cleanedVal = cleanLeadingZeros(newStringValue);
    newStringValue = cleanedVal.cleanedValue;

    let newCleanedStringValue = newStringValue.replace(/,/g, "");

    const isValidNum = isValidNumber(newCleanedStringValue);
    const zerosOnly = isOnlyZeros(newCleanedStringValue);

    if (rowsSize === 0) {
      if (ZEROS_LIST.includes(value) || zerosOnly) {
        dispatch(
          setScheduleOfValues({
            totalCostDisplay: value,
            totalCost: 0,
            remainingAmount: "―",
            remainingPercentage: "―",
          }),
        );
        return;
      }
    }

    if (!isValidNum) {
      return;
    }

    const newFloatValue = parseFloat(newCleanedStringValue);

    if (rowsSize > 0) {
      if (ZEROS_LIST.includes(newStringValue) || zerosOnly) {
        dispatch(
          setScheduleOfValues({
            totalCostDisplay: newStringValue,
          }),
        );

        if (invalidJobCostText === "") {
          setInvalidJobCostText(`Job cost can not be zero`);
        }
      } else {
        if (invalidJobCostText !== "") {
          setInvalidJobCostText("");
        }

        const newTotal = newFloatValue;
        const totalCents = Math.round(newTotal * 100);

        const newRows = rows?.map((item) => {
          const percent = parseFloat(item.percentage) || 0;
          const rowCents = Math.round((percent / 100) * totalCents);
          const newVal = rowCents / 100;

          return {
            ...item,
            amount: newVal,
            displayAmount: formatMoneyValue(String(newVal)),
          };
        });

        const currentTotalCents = newRows.reduce(
          (sum, item) => sum + Math.round((parseFloat(item.amount) || 0) * 100),
          0,
        );
        const diffCents = totalCents - currentTotalCents;

        if (diffCents !== 0 && newRows.length > 0) {
          const lastIndex = newRows.length - 1;
          const adjustedAmount = clampNearZero(
            (parseFloat(newRows[lastIndex].amount) || 0) + diffCents / 100,
          );

          newRows[lastIndex] = {
            ...newRows[lastIndex],
            amount: adjustedAmount,
            displayAmount: formatMoneyValue(String(adjustedAmount)),
          };
        }

        dispatch(setScheduleOfValues({ rows: newRows }));

        calculateRemainings({ newTotalCost: newTotal, newRows: newRows });
        dispatch(
          setScheduleOfValues({
            totalCost: newTotal,
            totalCostDisplay: newStringValue,
          }),
        );
      }
    } else {
      if (invalidJobCostText !== "") {
        setInvalidJobCostText("");
      }
      calculateRemainings({ newTotalCost: newFloatValue });

      dispatch(
        setScheduleOfValues({
          totalCost: newFloatValue,
          totalCostDisplay: String(newStringValue),
        }),
      );
    }

    setTimeout(() => {
      const newLength = newStringValue.length;
      const newCursor = Math.max(0, newLength - offsetFromEnd);

      if (input && selectionStart !== null) {
        input.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  function countDecimalPlaces(value) {
    if (Number.isInteger(value)) return 0;

    const str = value.toString();
    const parts = str.split(".");
    return parts[1]?.length || 0;
  }

  const handleInputChange = (index, field, value) => {
    const newRows = rows.map((r) => ({ ...r }));

    const usedTotalPercent = newRows
      .filter((_, i) => i !== index)
      .reduce((acc, r) => acc + (parseFloat(r.percentage) || 0), 0);

    const usedTotalAmount = newRows
      .filter((_, i) => i !== index)
      .reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);

    if (field === "title") {
      newRows[index][field] = capitalizeFirst(value);
    } else if (field === "percentage") {
      const input = document.getElementById(
        `row-percentage-input-${newRows[index].id}`,
      );
      const selectionStart = input?.selectionStart;

      const newPercentString = value.replace(/\s+/g, "");
      const newPercentFloat = parseFloat(newPercentString);

      const futureRemainingPercent =
        parseFloat(usedTotalPercent) + newPercentFloat;

      const newPercentDecimals = countDecimalPlaces(newPercentFloat);

      let dotWasRemoved = null;

      if (ZEROS_LIST.includes(value)) {
        newRows[index]["displayPercentage"] = value;
        newRows[index]["percentage"] = 0.0;
        newRows[index]["displayAmount"] = "0.00";
        newRows[index]["amount"] = 0.0;
      } else if (futureRemainingPercent <= 100 && newPercentDecimals <= 2) {
        const currentUsedInAmount = (newPercentFloat / 100) * totalCost;

        newRows[index]["displayPercentage"] = newPercentString;
        newRows[index]["percentage"] = newPercentFloat;
        newRows[index]["displayAmount"] = formatMoneyValue(
          String(currentUsedInAmount),
        );
        newRows[index]["amount"] = currentUsedInAmount;
      } else {
        const oldPercentString = newRows[index]["displayPercentage"] || "";
        dotWasRemoved =
          oldPercentString.includes(".") &&
          !newPercentString.includes(".") &&
          oldPercentString.replace(".", "") === newPercentString;
      }

      let newCursorPos = Math.max(selectionStart);

      if (/\s/.test(value)) {
        newCursorPos = Math.max(selectionStart - 1, 0);
      }
      if (dotWasRemoved) {
        newCursorPos = Math.max(selectionStart + 1, 0);
      }
      setTimeout(() => {
        if (input && selectionStart !== null) {
          input.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else if (field === "amount") {
      const input = document.getElementById(
        `row-amount-input-${newRows[index].id}`,
      );
      const selectionStart = input?.selectionStart;
      const originalLength = value.length;
      const originalCursor = selectionStart;
      const offsetFromEnd = originalLength - originalCursor;

      const cleanedString = value.replace(/\s+/g, "");
      const cleanedFloat = parseFloat(cleanedString);
      const totalFutureAmount = usedTotalAmount + cleanedFloat;
      const newPercentage = (cleanedFloat / totalCost) * 100;
      const newAmountDecimals = countDecimalPlaces(cleanedFloat);

      if (ZEROS_LIST.includes(value)) {
        newRows[index]["displayAmount"] = value;
        newRows[index]["percentage"] = 0.0;
        newRows[index]["displayAmount"] = "0.00";
        newRows[index]["amount"] = 0.0;
      } else if (totalFutureAmount <= totalCost && newAmountDecimals <= 2) {
        newRows[index]["displayAmount"] = value;
        newRows[index]["amount"] = isNaN(cleanedFloat) ? 0 : cleanedFloat;
        newRows[index]["percentage"] = newPercentage.toFixed(2);
        newRows[index]["displayPercentage"] = String(newPercentage.toFixed(2));
      }

      setTimeout(() => {
        const newLength = cleanedString.length;
        const newCursor = Math.max(0, newLength - offsetFromEnd);
        let finCursor = newCursor;

        if (totalFutureAmount > totalCost) {
          if (
            newRows[index]["displayAmount"].includes(".") &&
            !value.includes(".")
          ) {
            finCursor = finCursor + 1;
          } else {
            finCursor = finCursor - 1;
          }
        }
        if (input && selectionStart !== null) {
          input.setSelectionRange(finCursor, finCursor);
        }
      }, 0);
    }

    if (field !== "title") {
      calculateRemainings({ newRows });
    }
    dispatch(setScheduleOfValues({ rows: newRows }));
  };

  const handleDelete = useCallback(
    (indexes) => {
      const toDelete = Array.isArray(indexes) ? indexes : [indexes];
      const newRows = rows?.filter((_, i) => !toDelete.includes(i));

      // Calculate new current row index
      let newCurrentIndex = Math.min(...toDelete);
      if (newCurrentIndex >= newRows.length) {
        newCurrentIndex = newRows.length - 1;
      }

      calculateRemainings({ newRows });
      dispatch(setScheduleOfValues({ rows: newRows }));
      setCurrentRow(newCurrentIndex >= 0 ? [newCurrentIndex] : []);
    },
    [calculateRemainings, rows, dispatch],
  );

  useEffect(() => {
    if (rows?.length > 0) {
      const usedTotalCost = rows?.reduce(
        (acc, r) => acc + (parseFloat(r.amount) || 0),
        0,
      );

      if (usedTotalCost >= totalCost) {
        if (!addRowMax) {
          setAddRowMax(true);
        }
      } else {
        if (addRowMax) {
          setAddRowMax(false);
        }
      }
    } else {
      if (addRowMax) {
        setAddRowMax(false);
      }
    }
  }, [addRowMax, setAddRowMax, rows, totalCost]);

  useEffect(() => {
    if (rows?.length === 0 && totalCost > 0) {
      if (parseInt(remainingPercentage) !== 100) {
        const remainingFormatted = formatMoneyValue(String(totalCost));

        dispatch(
          setScheduleOfValues({
            remainingPercentage: 100,
            remainingAmount: remainingFormatted,
          }),
        );
      }
    }
  }, [remainingPercentage, rows?.length, totalCost, dispatch]);

  useEffect(() => {
    const handleDeleteSelected = (e) => {
      if (e.key === "Delete") {
        if (currentRow.length > 0) {
          handleDelete(currentRow);
        }
      }
    };

    window.addEventListener("keydown", handleDeleteSelected);
    return () => window.removeEventListener("keydown", handleDeleteSelected);
  }, [currentRow, handleDelete]);

  useEffect(() => {
    const handleArrowKeys = (e) => {
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      const isInputFocused =
        tag === "input" || tag === "textarea" || active?.isContentEditable;

      if (!isInputFocused) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setCurrentRow((prev) => {
            const next = Math.min(rows?.length - 1, (prev?.[0] ?? 0) + 1);
            return [next];
          });
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setCurrentRow((prev) => {
            const next = Math.max(0, (prev?.[0] ?? 0) - 1);
            return [next];
          });
        }
        return;
      }

      const id = active.id || "";
      const match = id.match(/^row-(title|percentage|amount)-input-(.+)$/);

      if (!match) return;

      const [, type, rowId] = match;
      const currentIndex = rows?.findIndex((r) => r.id === rowId);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (e.key === "ArrowDown") {
        nextIndex = Math.min(rows?.length - 1, currentIndex + 1);
      } else if (e.key === "ArrowUp") {
        nextIndex = Math.max(0, currentIndex - 1);
      } else {
        return;
      }

      e.preventDefault();

      const nextRowId = rows[nextIndex].id;
      const nextInput = document.getElementById(
        `row-${type}-input-${nextRowId}`,
      );
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
        setCurrentRow([nextIndex]);
      }
    };

    window.addEventListener("keydown", handleArrowKeys);
    return () => window.removeEventListener("keydown", handleArrowKeys);
  }, [rows]);

  useEffect(() => {
    const handleGlobalEnter = (e) => {
      if (e.key !== "Enter") return;
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      const isInputFocused =
        tag === "input" || tag === "textarea" || active?.isContentEditable;

      const rowIndex = currentRow?.[0];
      if (rowIndex === undefined) return;

      const row = rows[rowIndex];
      if (!row) return;

      if (!isInputFocused) {
        const titleInput = titleInputRef.current[row.id]?.title;
        if (titleInput) {
          e.preventDefault();
          titleInput.focus();
          titleInput.select();
        }
        return;
      }

      const id = active.id || "";
      const match = id.match(/^row-(title|percentage|amount)-input-(.+)$/);
      if (!match) return;

      const [, type, rowId] = match;
      const currentIndex = rows?.findIndex((r) => r.id === rowId);
      if (currentIndex === -1) return;

      e.preventDefault();

      const currentInput = document.getElementById(
        `row-${type}-input-${rowId}`,
      );

      if (currentInput) {
        currentInput.blur();
      }
    };

    window.addEventListener("keydown", handleGlobalEnter);
    return () => window.removeEventListener("keydown", handleGlobalEnter);
  }, [rows, currentRow]);

  useEffect(() => {
    const handlePlusKey = (e) => {
      if (e.key === "+") {
        const timer = setTimeout(() => {
          const tag = document.activeElement?.tagName?.toLowerCase();
          const isInputFocused =
            tag === "input" ||
            tag === "textarea" ||
            document.activeElement?.isContentEditable;

          if (!isInputFocused) {
            e.preventDefault();
            if (rows?.length === 23 || totalCost === 0 || addRowMax) return;
            handleAddRow();
          }
          return () => clearTimeout(timer);
        }, 0);
      }
    };

    window.addEventListener("keydown", handlePlusKey);
    return () => window.removeEventListener("keydown", handlePlusKey);
  }, [rows?.length, totalCost, addRowMax, handleAddRow]);

  const loadTemplate = async (template) => {
    const templateRows = template?.rows || [];
    const templateLogo = template?.logo_signed_url || template?.logo_url || "";

    const total = templateRows?.reduce(
      (sum, row) => sum + (parseFloat(row.amount) || 0),
      0,
    );

    await dispatch(
      setUsingTemplates({
        isUsingTemplate: true,
        templateID: template?.id,
        templateName: template?.template_name,
        removedTemplate: false,
      }),
    );
    await dispatch(
      setScheduleOfValues({
        rows: templateRows,
        remainingAmount: 0.0,
        remainingPercentage: 0.0,
        totalCost: total,
        totalCostDisplay: formatMoneyValue(String(total)),
        loadedLogo: templateLogo,
      }),
    );
  };

  const submitSaveTemplate = async () => {
    const cleanedTemplateName = tempTemplateName.trim();

    if (!cleanedTemplateName || cleanedTemplateName.length < 4) {
      return;
    }

    const saved = await dispatch(
      handleSaveScheduleValuesTemplate(cleanedTemplateName),
    );

    if (saved) {
      await dispatch(fetchSovTemplates());
      setShowSaveTemplate(false);
      setTempTemplateName("");
    }
  };

  const submitRemoveTemplate = async () => {
    await dispatch(handleRemoveTemplate());
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "calc(100vh - 100px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            pt: 2,
          }}
          gap={6}
        >
          <Typography variant="h6" color="primary" gutterBottom>
            Scope of Work
          </Typography>
          {isUsingTemplate && (
            <Box
              sx={{
                position: "relative",
                px: 2,
                py: 0.5,
                border: removedTemplate
                  ? "2px dashed #b71c1c"
                  : "2px dashed #2e7d32",
                borderRadius: "2px",
                backgroundColor: removedTemplate
                  ? "rgba(183,28,28,0.05)"
                  : "rgba(46,125,50,0.06)",
                color: removedTemplate ? "#b71c1c" : "#1b5e20",
                fontWeight: removedTemplate ? 900 : 700,
                fontSize: removedTemplate ? "0.85rem" : "0.8rem",
                textTransform: "uppercase",
                letterSpacing: removedTemplate ? "1.5px" : "1px",
                transform: "rotate(-2deg)",
                display: "flex",
                alignItems: "center",
                fontFamily: `'Courier New', Courier, monospace`,
                boxShadow: removedTemplate
                  ? "2px 2px 4px rgba(0,0,0,0.25)"
                  : "1px 1px 2px rgba(0,0,0,0.2)",

                overflow: removedTemplate ? "visible" : "hidden",
                minHeight: removedTemplate ? "32px" : undefined,
              }}
            >
              <Box component="span">
                {removedTemplate ? "Removed Template:" : "Using Template:"}
              </Box>

              <Box
                component="span"
                sx={{
                  fontWeight: 900,
                  color: removedTemplate ? "#7f0000" : "#0d4d18",
                  ml: 4,
                  mr: removedTemplate ? 0 : 1,
                  letterSpacing: "1px",
                  textDecoration: removedTemplate ? "line-through" : "none",
                  textDecorationThickness: "2px",
                  textDecorationColor: "#c62828",
                }}
              >
                {templateName}
              </Box>

              {!removedTemplate && (
                <Tooltip title="Remove Template">
                  <DeleteIcon
                    onClick={submitRemoveTemplate}
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                      color: "#a00",
                      "&:hover": { color: "#d00" },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            textAlign: "center",
            position: "absolute",
            right: 20,
            top: 5,
          }}
        >
          <Box
            sx={{
              borderRadius: 1,
              boxShadow: "1px 2px 5px rgba(0, 0, 0, 0.3)",
              minWidth: 80,
              minHeight: 80,
              display: "flex", // 💥 Make it flex
              justifyContent: "center", // 💥 Horizontal center
              alignItems: "center", // 💥 Vertical center
            }}
          >
            {hasLogo ? (
              <img
                src={Boolean(loadedLogo) ? loadedLogo : logoPreview}
                alt="Logo Preview"
                style={{
                  maxWidth: 80,
                  maxHeight: 80,
                }}
              />
            ) : (
              <FormHelperText
                fontWeight="bold"
                color="primary"
                sx={{ textAlign: "center" }}
              >
                Upload Your <br /> Logo
              </FormHelperText>
            )}
          </Box>
        </Box>

        <Box
          gap={1}
          sx={{
            display: "flex",
            mb: 1,
            mt: 5,
            px: 2,
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <FormControl
              disabled={savingTemplate}
              variant="outlined"
              error={Boolean(invalidJobCostText)}
            >
              <InputLabel
                htmlFor="outlined-adornment-amount"
                sx={{
                  backgroundColor: "white", // or your form background
                  px: 0.5, // padding to match notch
                  zIndex: 1,
                }}
              >
                Total Job Cost
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-amount"
                value={totalCostDisplay}
                onChange={(e) => {
                  handleChangeAmount(e.target.value);
                }}
                onBlur={(e) => handleBlur(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "+") {
                    e.preventDefault();
                    e.currentTarget.blur();
                    setTimeout(() => {
                      const justZeros = isOnlyZeros(String(totalCost));
                      if (
                        rows?.length === 23 ||
                        totalCost === 0 ||
                        addRowMax ||
                        justZeros
                      ) {
                        return;
                      } else {
                        handleAddRow();
                      }
                    }, 100);
                  }
                }}
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
              />
              <FormHelperText
                sx={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  mt: 0.5,
                  fontWeight: "bold",
                }}
              >
                {invalidJobCostText}
              </FormHelperText>
            </FormControl>
          </Box>
          <Button
            disabled={
              rows?.length === 23 ||
              totalCost === 0 ||
              addRowMax ||
              savingTemplate
            }
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ pl: 0.8 }}
            onClick={handleAddRow}
          >
            Add Row
          </Button>
        </Box>

        <Box
          gap={1}
          sx={{
            display: "flex",
            mb: 1,

            px: 2,
            alignItems: "center",
          }}
        >
          <Button
            disabled={savingTemplate}
            size="small"
            color={logoUploaded || Boolean(loadedLogo) ? "error" : "primary"}
            variant={
              logoUploaded || Boolean(loadedLogo) ? "contained" : "outlined"
            }
            endIcon={
              logoUploaded || Boolean(loadedLogo) ? (
                <DeleteIcon />
              ) : (
                <UploadIcon />
              )
            }
            onClick={() => {
              if (logoUploaded || Boolean(loadedLogo)) {
                setLogoUploaded(false);
                setLogoPreview(null);
                setFile(null);
                if (Boolean(loadedLogo)) {
                  dispatch(
                    setScheduleOfValues({
                      loadedLogo: "",
                    }),
                  );
                }
                if (fileInputRef.current) fileInputRef.current.value = null;
              } else {
                fileInputRef.current?.click();
              }
            }}
            sx={{ mr: 1 }}
          >
            {logoUploaded || Boolean(loadedLogo)
              ? "Remove Logo"
              : "Upload Logo"}
          </Button>
          <Box sx={{ position: "relative" }}>
            <Button
              disabled={savingTemplate}
              size="small"
              variant="contained"
              endIcon={<ArrowDropDownIcon />}
              sx={{ pr: 0.8, mr: 1, backgroundColor: "primary.main" }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              Templates
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
            >
              {sovTemplates && sovTemplates.length > 0 ? (
                sovTemplates.map((template, idx) => (
                  <MenuItem
                    key={idx}
                    onClick={() => {
                      setAnchorEl(null);
                      loadTemplate(template);
                    }}
                  >
                    {template?.template_name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ opacity: 0.6, fontStyle: "italic" }}>
                  No templates found
                </MenuItem>
              )}
            </Menu>
          </Box>

          {showSaveTemplate && (
            <FormControl
              disabled={savingTemplate || savingTemplateLoading}
              variant="outlined"
              size="small"
              sx={{ mr: 1, minWidth: 180 }}
            >
              <OutlinedInput
                inputRef={saveTemplateInputRef}
                placeholder="Template name"
                value={tempTemplateName}
                onChange={(e) => setTempTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitSaveTemplate();
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    setShowSaveTemplate(false);
                    setTempTemplateName("");
                  }
                }}
              />
            </FormControl>
          )}

          <Button
            disabled={
              savingTemplate ||
              savingTemplateLoading ||
              rows?.length <= 0 ||
              (showSaveTemplate && tempTemplateName.trim().length < 4)
            }
            size="small"
            color="success"
            variant={showSaveTemplate ? "contained" : "outlined"}
            sx={{ mr: 1 }}
            onClick={() => {
              if (!showSaveTemplate) {
                if (isUsingTemplate && templateName) {
                  setTempTemplateName(templateName);
                }

                setShowSaveTemplate(true);

                setTimeout(() => {
                  saveTemplateInputRef.current?.focus();
                  saveTemplateInputRef.current?.select?.();
                }, 50);

                return;
              }

              submitSaveTemplate();
            }}
          >
            {savingTemplateLoading
              ? "Saving..."
              : showSaveTemplate
                ? "Submit"
                : "Save Template"}
          </Button>

          {showSaveTemplate && (
            <Button
              disabled={savingTemplateLoading}
              size="small"
              color="error"
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => {
                setShowSaveTemplate(false);
                setTempTemplateName("");
              }}
            >
              Cancel
            </Button>
          )}
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Table size="small" stickyHeader sx={{ px: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#004976 !important",
                    color: "white !important",
                    fontWeight: "bold !important",
                    width: "20px",
                  }}
                />
                <TableCell
                  sx={{
                    backgroundColor: "#004976 !important",
                    color: "#fff !important",
                    fontWeight: "bold !important",

                    textAlign: "center",
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#004976 !important",
                    color: "#fff !important",
                    fontWeight: "bold !important",
                    width: "90px",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Percentage
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#004976 !important",
                    color: "#fff !important",
                    fontWeight: "bold !important",
                    width: "100px",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#004976 !important",
                    color: "#fff !important",
                    fontWeight: "bold !important",
                    width: "20px",
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
          </Table>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement]}
          >
            <Box ref={scrollRef} sx={{ overflowY: "auto", flex: 1, mx: 1 }}>
              <Table size="small">
                <TableBody>
                  <SortableContext
                    items={rows?.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {rows?.map((row, index) => (
                      <SortableRow
                        key={row.id}
                        id={row.id}
                        index={index}
                        row={row}
                        handleInputChange={handleInputChange}
                        handleDeleteSingle={handleDeleteSingle}
                        currentRow={currentRow}
                        setCurrentRow={setCurrentRow}
                        titleInputRef={titleInputRef}
                        formatMoneyValue={formatMoneyValue}
                        editableRows={editableRows}
                        setEditableRows={setEditableRows}
                        handleAddRow={handleAddRow}
                        addRowMax={addRowMax}
                        totalCost={totalCost}
                        savingTemplate={savingTemplate}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </Box>
          </DndContext>
        </TableContainer>
        <Box
          gap={6}
          sx={{
            backgroundColor: "white",
            borderTop: "2px solid #004976",
            py: 1,
            mx: 2,
            pr: 5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ color: "#004976" }}
          >
            Remaining:{" "}
            <span style={{ paddingLeft: "1.5rem" }}>
              {typeof remainingPercentage === "number"
                ? remainingPercentage.toFixed(2)
                : "―"}
            </span>
            <span style={{ paddingLeft: "0.5rem" }}>%</span>
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ color: "#004976" }}
          >
            <span style={{ paddingRight: "0.5rem" }}>$</span>
            {remainingAmount === "―"
              ? "―"
              : formatMoneyValue(String(remainingAmount))}
          </Typography>
        </Box>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            const uploadedFile = e.target.files?.[0];
            if (!uploadedFile) {
              setFile(null);
              return;
            }

            setFile(uploadedFile);

            const reader = new FileReader();
            reader.onload = function (event) {
              const img = new Image();
              img.onload = function () {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                // Resize logic (max width/height: 150px)
                const maxSize = 150;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                  if (width > maxSize) {
                    height = (height * maxSize) / width;
                    width = maxSize;
                  }
                } else {
                  if (height > maxSize) {
                    width = (width * maxSize) / height;
                    height = maxSize;
                  }
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                const dataUrl = canvas.toDataURL("image/png");
                setLogoPreview(dataUrl);
                setLogoUploaded(true);
                // Sync uploaded logo into Redux so parent can submit it
                dispatch(
                  setScheduleOfValues({
                    loadedLogo: dataUrl,
                  }),
                );
              };
              img.src = event.target.result;
            };
            reader.readAsDataURL(uploadedFile);
          }}
        />
      </Box>
    </>
  );
};

export default CreatingScheduleOfValues;
